import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IAppointment {
  id: string;
  clientId: string;
  coachId: string;
  startDate: string;
  endDate: string;
  title: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  isCancelled: boolean;
  profilePic: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  platform: string;
  meetingURL: string;
}

export interface ICreateAppointmentInput {
  id?: string;
  clientId: string;
  coachId: string;
  startDate: string;
  endDate: string;
  title: string;
  platform: string;
  description?: string;
  meetingURL?: string;
  createdAt?: string;
  updatedAt?: string;
  isCancelled?: boolean;
  profilePic?: string;
  clientFirstName?: string;
  clientLastName?: string;
  clientEmail?: string;
}

interface IAppointmentState {
  appointments: IAppointment[];
  fetchAppointmentsLoading: boolean;
  selectedClientAppointments: IAppointment[];
}

export interface IFetchGetAppointmentsForCoachInput {
  coachId: string;
  from: string;
  to: string;
}

export interface IFetchGetAppointmentsForClientInput {
  clientId: string;
  from: string;
  to: string;
}

const initialState: IAppointmentState = {
  appointments: [],
  fetchAppointmentsLoading: true,
  selectedClientAppointments: [],
};

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    fetchAppointmentsRequest: (
      state: IAppointmentState,
      _action: PayloadAction<IFetchGetAppointmentsForCoachInput>
    ) => {
      state.fetchAppointmentsLoading = true;
    },
    fetchAppointmentsSuccess: (state: IAppointmentState, action: PayloadAction<IAppointment[]>) => {
      state.fetchAppointmentsLoading = false;
      state.appointments = action.payload;
    },
    fetchAppointmentsFailure: (state: IAppointmentState, _action: PayloadAction<string>) => {
      state.fetchAppointmentsLoading = false;
      state.appointments = [];
    },
    createAppointmentRequest: (
      state: IAppointmentState,
      _action: PayloadAction<ICreateAppointmentInput>
    ) => {
      state.fetchAppointmentsLoading = true;
    },
    createAppointmentSuccess: (state: IAppointmentState, action: PayloadAction<IAppointment>) => {
      state.fetchAppointmentsLoading = false;
      state.appointments.push(action.payload);
    },
    createAppointmentFailure: (state: IAppointmentState, _action: PayloadAction<string>) => {
      state.fetchAppointmentsLoading = false;
    },
    deleteAppointmentRequest: (state: IAppointmentState, _action: PayloadAction<string>) => {
      state.fetchAppointmentsLoading = true;
    },
    deleteAppointmentSuccess: (state: IAppointmentState, action: PayloadAction<string>) => {
      state.fetchAppointmentsLoading = false;
      state.appointments = state.appointments.filter(
        (appointment) => appointment.id !== action.payload
      );
    },
    deleteAppointmentFailure: (state: IAppointmentState, _action: PayloadAction<string>) => {
      state.fetchAppointmentsLoading = false;
    },
    editAppointmentRequest: (
      state: IAppointmentState,
      _action: PayloadAction<ICreateAppointmentInput>
    ) => {
      state.fetchAppointmentsLoading = true;
    },
    editAppointmentSuccess: (state: IAppointmentState, action: PayloadAction<IAppointment>) => {
      state.fetchAppointmentsLoading = false;
      state.appointments = state.appointments.map((appointment) => {
        if (appointment.id === action.payload.id) {
          return action.payload;
        }
        return appointment;
      });
    },
    editAppointmentFailure: (state: IAppointmentState, _action: PayloadAction<string>) => {
      state.fetchAppointmentsLoading = false;
    },
    fetchSelectedClientAppointmentsRequest: (
      state: IAppointmentState,
      _action: PayloadAction<IFetchGetAppointmentsForClientInput>
    ) => {
      state.fetchAppointmentsLoading = true;
    },
    fetchSelectedClientAppointmentsSuccess: (
      state: IAppointmentState,
      action: PayloadAction<IAppointment[]>
    ) => {
      state.fetchAppointmentsLoading = false;
      state.selectedClientAppointments = action.payload;
    },
    fetchSelectedClientAppointmentsFailure: (
      state: IAppointmentState,
      _action: PayloadAction<string>
    ) => {
      state.fetchAppointmentsLoading = false;
    },
  },
});

export const {
  fetchAppointmentsRequest,
  fetchAppointmentsSuccess,
  fetchAppointmentsFailure,
  createAppointmentRequest,
  createAppointmentSuccess,
  createAppointmentFailure,
  deleteAppointmentRequest,
  deleteAppointmentSuccess,
  deleteAppointmentFailure,
  editAppointmentRequest,
  editAppointmentSuccess,
  editAppointmentFailure,
  fetchSelectedClientAppointmentsRequest,
  fetchSelectedClientAppointmentsSuccess,
  fetchSelectedClientAppointmentsFailure,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
